import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/ui";
import { Heart, Star, Calendar, Users } from "lucide-react";
import { useUnsplashImages } from "@/hooks/useUnsplashImages";
import { Link } from 'react-router-dom';

const KidsGyms = () => {
  const [heroImage, setHeroImage] = useState("");
  const { data: gymImages } = useUnsplashImages("children gym fitness", 1);
  
  useEffect(() => {
    if (gymImages && gymImages.length > 0) {
      setHeroImage(gymImages[0].urls.regular);
    }
  }, [gymImages]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Kids Gym Management Software - Hivetivity</title>
        <meta name="description" content="Streamline your kids' gym operations with Hivetivity. Manage classes, track fitness goals, and communicate with parents efficiently." />
      </Helmet>

      <TopNavbar />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-b from-red-50 to-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#5a5a59] mb-6">
                  Power Up Your Kids' Gym Management
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Focus on building healthy, active kids while we handle the administrative heavy lifting with our comprehensive platform.
                </p>
                <Link to="/business-trial">
                  <Button className="bg-red-500 hover:bg-red-600 text-white px-8 py-6 text-lg">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
              <div className="relative">
                {heroImage ? (
                  <OptimizedImage 
                    src={heroImage}
                    alt="Kids enjoying fitness activities"
                    width={600}
                    height={400}
                    className="rounded-2xl shadow-xl"
                    objectFit="cover"
                  />
                ) : (
                  <div className="rounded-2xl bg-gray-200 w-full h-[400px] animate-pulse flex items-center justify-center">
                    <p className="text-gray-500">Loading image...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-[#5a5a59] mb-12">
              Everything Your Kids' Gym Needs
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="p-6 rounded-xl bg-red-50 hover:bg-red-100 transition-colors">
                <Users className="w-12 h-12 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Class Management</h3>
                <p className="text-gray-600">Organize classes by age and skill level.</p>
              </div>
              <div className="p-6 rounded-xl bg-red-50 hover:bg-red-100 transition-colors">
                <Star className="w-12 h-12 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Progress Tracking</h3>
                <p className="text-gray-600">Monitor fitness goals and achievements.</p>
              </div>
              <div className="p-6 rounded-xl bg-red-50 hover:bg-red-100 transition-colors">
                <Calendar className="w-12 h-12 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Smart Scheduling</h3>
                <p className="text-gray-600">Manage class times and instructor assignments.</p>
              </div>
              <div className="p-6 rounded-xl bg-red-50 hover:bg-red-100 transition-colors">
                <Heart className="w-12 h-12 text-red-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Health Tracking</h3>
                <p className="text-gray-600">Record and monitor fitness milestones.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-20 bg-red-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-[#5a5a59] mb-8">
                Loved by Kids' Gym Owners
              </h2>
              <blockquote className="text-xl text-gray-600 italic mb-6">
                "Hivetivity has revolutionized how we run our kids' gym. The class management and progress tracking features are perfect for our needs."
              </blockquote>
              <p className="font-semibold text-[#5a5a59]">
                - James Wilson, FitKids Gym Owner
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-[#5a5a59] mb-6">
              Ready to Transform Your Kids' Gym?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join successful kids' gyms using Hivetivity to create fun and healthy fitness programs.
            </p>
            <Link to="/business-trial">
              <Button className="bg-red-500 hover:bg-red-600 text-white px-8 py-6 text-lg">
                Start Your Free Trial Today
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default KidsGyms;
